import React from 'react';
import { InjectedRouter } from 'react-router';
import once from 'lodash/once';
import Box from 'components/Box';
import { QueryResult, OperationVariables } from 'react-apollo';
import Button from 'components/Button';
import BoxBody from 'components/BoxBody';
import Buttons from 'components/Buttons';
import BoxHeader from 'components/BoxHeader';
import { withRouterPropTyped } from 'app/context/Router';
import IntegrationSettingsNotFound from './IntegrationSettingsNotFound';
import { HoCQueryComponent } from 'types/typescript/util';

import INTEGRATION_SETTINGS_INDEX_QUERY from '../queries/IntegrationSettingsIndexQuery.gql';
import { IntegrationSettingsIndex as IntegrationSettingsIndexData } from '../queries/__generated__/IntegrationSettingsIndex';

import './styles/IntegrationSettingsIndex.scss';

type IncomingProps = {
  router: InjectedRouter;
};

type Props = {
  apolloProps: QueryResult<IntegrationSettingsIndexData>;
} & IncomingProps;

const isAuthorized = (o: any) => o.permissions.canEditDeveloperSettings;

class IntegrationSettingsIndex extends React.Component<Props & IncomingProps, {}, {}> {
  redirectToSettings = once((o: any) => this.props.router.replace(`/integrations/${o.uuid}`));

  renderOrganization = (o: any) => {
    return (
      <Button
        key={o.uuid}
        className="IntegrationSettingsIndex-button"
        onClick={() => this.redirectToSettings(o)}>
        {o.name}
      </Button>
    );
  };

  render() {
    const loading = this.props.apolloProps.loading;
    const organizations = this.props.apolloProps.data?.me?.organizations || [];
    const editableOrganizations = organizations.filter(isAuthorized);

    if (loading) {
      return null;
    }

    if (editableOrganizations.length === 0) {
      return <IntegrationSettingsNotFound />;
    }

    if (editableOrganizations.length === 1) {
      this.redirectToSettings(editableOrganizations[0]);
    }

    return (
      <div className="IntegrationSettingsIndex">
        <Box size="medium">
          <BoxHeader title="You have access to multiple organizations settings" />
          <BoxBody>
            <Buttons axis="y">{editableOrganizations.map(this.renderOrganization)}</Buttons>
          </BoxBody>
        </Box>
      </div>
    );
  }
}

/********
 * ok goodness gracious what is happening here:
 * This is utilizing HoCQueryComponent to more easily replace the "graphql()" Higher Order Component mechanism.
 *  Under the hood it is recreating the HoQ with the <Query> component whcih allows for much more sane typing.
 *    "graphql()"  was impossible to type because it can be either a query or a mutation and is just a mess
 *      (was made before typing got serious)
 *
 *  Let's breakdown how this works, because there are a lot of nested functions here
 *    I've purposefully assigned each stage of this so that you can see the typing and what is being passed around
 *
 *    The first section sets the typing for everything.
 *      type1 (TData) is the data we want from graphql in the return, just set that to what we spit out in the generated types
 *      type2 (TVariables)  this is a query that has variables, assign those here, they will be in the globalType file.
 *                          Please note: the variables would go in the first set of props in function1
 *                            along with query just like a normal <Query> component.
 *                            Think of the parameters of this function as the props for <Query> (they the same, except no children)
 *                            "OperationVariables" are necessary in the absence of variables, otherwise use the generated ones.
 *      type3 (IncomingProps)  These will be the props MINUS the data prop that we expect the component to have
 *          (this will allow the types to be passed forward, we previously lost all typing with the "graphql" HoC)
 *
 *      function1 What we create here is actually the HoC creator method that will take in a wrapped
 *        component type and attach all the graphql query data to it
 *
 *       function2 is the "actual" higher order component function creator (take in props, spit our JSX.Element)
 *
 *
 *       and finally we call withRouterPropType (which actually types the darn thing) and we spit out a type of
 *        React.ComponentType<IncomingProps> , WHICH IS ACTUALLY PROPERLY EXTERNALLY TYPED!!!
 *          WE NOW HAVE TYPE SAFETY, TYPESCRIPT, AND WITHOUT HAVING TO DRASTICALLY CHANGE THESE TYPES OF FILES
 *            OR RE-WRITE THE TESTS BECAUSE OF CHANGING THINGS TO <QUERY>
 *
 */

// typing setup
const HoCQuery1 = HoCQueryComponent<
  IntegrationSettingsIndexData,
  OperationVariables,
  IncomingProps
>(
  // function 1
  { query: INTEGRATION_SETTINGS_INDEX_QUERY }
);
// function 2
const HoCQuery2 = HoCQuery1(IntegrationSettingsIndex);

const HoQQueryWithRouter = withRouterPropTyped(HoCQuery2);

// THIS WAS INTENTIONALLY SPLIT OUT TO MAKE IT OBVIOUS WHAT TYPES ARE TO THE READER
// Other components may use it like thus:
/*
      HoCQueryComponent<IntegrationSettingsIndexData, OperationVariables, IncomingProps>
        ({query: INTEGRATION_SETTINGS_INDEX_QUERY})
        (IntegrationSettingsIndex)
 * */

export { IntegrationSettingsIndex };
export default HoQQueryWithRouter;
